import { useReduxState } from 'hooks';
import { ErrorPage } from 'components/error';
import {
  Page,
  PageContent,
  PageHeader,
  PageInnerContent,
  PageSidebar,
  PageSidebarTitle
} from 'components/page';
import { CopyButtonText, Owners } from 'components/common';

import type { NavLinkOrSubheading } from 'types';

interface Props {
  activeLink?: string;
  children: React.ReactNode;
}

const CalendarEventPage = ({ activeLink = '', children }: Props) => {
  const {
    messages,
    pageState: { calendarEvent },
    serverConfig: { adminUrl, featureFlags },
    user
  } = useReduxState();

  if (!calendarEvent)
    return (
      <ErrorPage
        description={messages.errorNotFound}
        icon={['far', 'calendar-days']}
        title={messages.eventCalendarEventNotFound}
      />
    );

  const userIsOwnerOrSuperadmin =
    calendarEvent.userIsOwner || user?.roles.includes('superadmin');

  // a participant may reach the feedback sub-page (but no other sub-page)
  // once the form is actually something they are allowed to respond to
  const participantCanAccessFeedback =
    featureFlags.enableFeedbackForCalendarEvents &&
    calendarEvent.feedbackEnabled &&
    calendarEvent.hasFeedbackForm &&
    Boolean(user) &&
    !user?.roles.includes('guest') &&
    (calendarEvent.feedbackAllowNonParticipated ||
      calendarEvent.userParticipated);

  const isFeedbackLink = activeLink === '/feedback';

  if (
    !userIsOwnerOrSuperadmin &&
    activeLink &&
    !(isFeedbackLink && participantCanAccessFeedback)
  ) {
    return (
      <ErrorPage
        description={messages.errorPermission}
        icon={['far', 'calendar-days']}
        title={messages.errorForbidden}
      />
    );
  }

  const navActiveLink = `/calendar/${calendarEvent.id}${activeLink}`;

  const feedbackNavLink = {
    href: `/calendar/${calendarEvent.id}/feedback`,
    icon: 'bullhorn' as const,
    title: messages.commonFeedback
  };

  const navLinks: NavLinkOrSubheading[] = [
    {
      href: `/calendar/${calendarEvent.id}`,
      icon: 'home',
      title: messages.eventCalendarEventPage
    },
    ...(userIsOwnerOrSuperadmin
      ? [
          {
            href: `/calendar/${calendarEvent.id}/settings`,
            icon: 'cog' as const,
            title: messages.commonSettings
          },
          {
            href: `/calendar/${calendarEvent?.id}/participant-settings`,
            icon: 'user-cog' as const,
            title: messages.eventCalendarParticipantsSettings
          },
          {
            href: `/calendar/${calendarEvent.id}/competencies`,
            icon: 'medal' as const,
            title: messages.commonCompetencies
          },
          {
            href: `/calendar/${calendarEvent?.id}/access`,
            icon: 'eye' as const,
            title: messages.eventCalendarSettingsAccess
          },
          {
            href: `/calendar/${calendarEvent.id}/participants`,
            icon: 'users' as const,
            title: messages.commonParticipants,
            unreadItems: calendarEvent.numberOfPendingAccessRequests
          },
          ...(featureFlags.enableFeedbackForCalendarEvents
            ? [feedbackNavLink]
            : []),
          {
            href: `/calendar/${calendarEvent.id}/events`,
            icon: 'list' as const,
            title: messages.commonActivityLog
          },
          {
            alignRight: true,
            href: `/calendar/${calendarEvent.id}/admin`,
            icon: 'user-shield' as const,
            title: messages.pagesManage
          }
        ]
      : participantCanAccessFeedback
      ? [feedbackNavLink]
      : [])
  ];

  return (
    <Page activePathname={'/calendar'}>
      <PageHeader
        backLinkAs="/calendar"
        backLinkHref="/calendar"
        title={messages.eventCalendar}
      />

      <PageContent withSidebar={true}>
        <PageSidebar active={navActiveLink} isWide={true} links={navLinks}>
          <PageSidebarTitle
            icon={['far', 'calendar-days']}
            subtitle={
              <>
                <Owners
                  owners={calendarEvent.owners}
                  style={{ width: '100%' }}
                />
                {userIsOwnerOrSuperadmin ? (
                  <CopyButtonText
                    animateToBottom={true}
                    successText={messages.commonCopied}
                    style={{ marginTop: 2, marginBottom: 0, padding: 0 }}
                    text={messages.commonCopyLink}
                    value={`${adminUrl}/calendar/${calendarEvent.id}`}
                  />
                ) : null}
              </>
            }
            title={calendarEvent.title}
          />
        </PageSidebar>
        <PageInnerContent>{children}</PageInnerContent>
      </PageContent>
    </Page>
  );
};

export default CalendarEventPage;
